/* eslint-disable no-nested-ternary */
import { lazy, useEffect, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { AlertDialogComponent } from '@mesh.ai/rabbit';
import '@mesh.ai/rabbit/dist/theme/deer-variables.css';
import { ScrollContext } from 'App';
import { NON_DEER_ROUTES } from 'Deer/constants/nonDeerRoutes';
import { sendRequest } from 'api/sendRequest';
import DatePickerComponentWithRedux from 'components/DatePickerComponentWithRedux';
import DateTimePickerComponentWithRedux from 'components/DateTimePickerComponentWithRedux';
import { CompetencyDrawer } from 'components/Develop/CompetencyDrawer';
import GifModal from 'components/Gif/GifModal';
import { GoalsWeightProvider } from 'components/GoalsWeight/GoalsWeightContext';
import GoalsWeightSidepanelV1 from 'components/GoalsWeight/GoalsWeightSidepanelV1';
import PrivateRoute from 'components/PrivateRoute';
import { ViewsWrapper } from 'components/Views/ViewsWrapper';
// import WindowInnerHeightLogger from 'developmentHelpers/WindowInnerHeightLogger';
import { useDispatch } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useUnsavedDataAlertProps } from 'reducers/unsavedDataAlertSlice';
import { NoMobilePageSupportNetchexMessage } from 'screens/NonAuthenticatedNetchexMessage/NonAuthenticatedNetchexMessage';
import { DeerBanner } from 'screens/admin/NewUX/DeerBanner';
import { CreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer';
import ContinuousFeedbackSidebar from 'screens/performanceReview/components/ContinuousFeedback';
import { NominatePeersSidePanelV1 } from 'screens/performanceReview/components/nominatePeersSidePanelV1/NominatePeersSidePanelV1';
import { selectEnabledFeatureFlags } from 'selectors/enabledFeatureFlags';
import store from 'store';
import {
  checkNewNavPreviewFeatureEnabled,
  checkVisierAnalyticsEnabled,
  checkVisierReportsEnabled,
} from 'utils/helpers/featureFlags';
import { retry } from 'utils/retry';

import { FEATURE_FLAGS } from 'constants/featureFlags';
import { isMobileView } from 'constants/isMobileView';
import {
  NETCHEX_NEW_ICON_ONLY_SIDEPANEL_WIDTH,
  NETCHEX_SIDEPANEL_WIDTH,
  isNetchexLogin,
} from 'constants/netChexConstants';

import { useSelectIsExpanded } from 'uiToolkitV1/ExpandableContainerProvider/ExpandedContainerSlice';

import { CreateViolationDrawer } from './components/DisciplinaryAction/BottomDrawers/CreateViolationDrawer/CreateViolationDrawer';
import { ViewViolationDrawer } from './components/DisciplinaryAction/BottomDrawers/ViewViolationDrawer';
import { GlobalSearch } from './components/GlobalSearch/GlobalSearch';
import { GoalBulkUploadPanel } from './components/Goals/BulkUploadPanels/GoalBulkUploadPanel';
import { ObjectiveBulkUploadPanel } from './components/Goals/BulkUploadPanels/ObjectiveBulkUploadPanel';
import { VisierLogin } from './components/Visier/VisierLogin';
import LeftNav from './components/navigation';
import { NetchexPrimaryNav } from './components/navigation/primary/NetchexPrimaryNav';
import { deerEndpoints } from './constants/endpoints';
import { L1_PATHS, staticRoutes } from './constants/routes';
import { getUserL1Access, getUserL2Access } from './helpers/getUserAccess';
import { useDeerRabbitRedirection } from './hooks/useDeerRabbitRedirection/useDeerRabbitRedirection';
import { useOneOnOneRedirection } from './hooks/useDeerRabbitRedirection/useOneOnOneRedirection';
import { useIsNetchexMobileSupportedPage } from './hooks/useIsNetchexMobileSupportedPage';
import { useIsNetchexNewNavAndAdminOrManagerPage } from './hooks/useIsNetchexNewNavAndAdminOrManagerPage';
import { useIsNetchexNewSidebarInternalPage } from './hooks/useIsNetchexNewSidebarInternalPage';
import { useLoggedInUsersAccessesQuery } from './hooks/useLoggedInUsersAccessesQuery';
import { useRouteToNavLevels } from './hooks/useRouteToL3';
import { useScrollToTopOnPathChange } from './hooks/useScrollToTopOnPathChange';
import Company from './pages/Company';
import { AddQuestionBottomDrawer } from './pages/Company/Engagement/Questions/AddQuestionBottomDrawer';
import Home from './pages/Home';
import MyData from './pages/MyData';
import { PageNotFound404 } from './pages/PageNotFound404';
import { Profile } from './pages/Profile';
import Team from './pages/Team';
import { setL2Visible, useSelectIsL2Visible } from './slice/DeerNav.slice';
import { setVisierAuthToken } from './slice/UserAccessesSlice';
import './styles/Deer.css';

const OKRCreationPanel = lazy(() => retry(() => import('components/RabbitOKRCreationPanel/OKRCreationPanel')));
const OKRSidePanel = lazy(() => retry(() => import('components/RabbitOKRSidePanel')));
const InitiativeSidePanel = lazy(() => retry(() => import('components/RabbitInitiativeSidePanel')));
const InitiativeCreationPanel = lazy(() =>
  retry(() => import('components/RabbitInitiativeCreationPanel/InitiativeCreationPanel'))
);
const QuestionDetailsSidePanel = lazy(() => retry(() => import('screens/admin/common/QuestionDetailsSidePanel')));
const CalibrationViewSidepanel = lazy(() =>
  retry(() =>
    import(
      'screens/performanceReview/manageAndInsights/TalentPlanning/CalibrationViewSidepanel/CalibrationViewSidepanel'
    ).then((module) => ({
      default: module.CalibrationViewSidepanel,
    }))
  )
);
const ReviewManageSidepanel = lazy(() =>
  retry(() =>
    import('screens/performanceReview/manageAndInsights/ReviewManage/reviewManageSidepanel/ReviewManageSidepanel').then(
      (module) => ({
        default: module.ReviewManageSidepanel,
      })
    )
  )
);

type AppProps = {
  isAuthenticated: boolean;
};

export const L1_L2_NAV_WIDTH = '336px';

const StyledRightContentBox = styled(Box)`
  background-color: ${(props) => (props.isNetchexEmployeeNavbar ? '#F9FAFB' : '#f5f7f9')};
  border-right: ${(props) => (props.isNetchexEmployeeNavbar ? '1px solid #E9E9E9' : 'none')};
  min-height: 100vh;
  position: relative;
  z-index: 1; // for the sidebar collapse button to not be hidden behind the right content
  &.right-content-root {
    width: ${(props) => (props.isNetchexEmployeeNavbar ? '100vw' : 'calc(100vw - 80px)')};
  }
  &.right-content-root-mobile {
    width: 100vw;
  }

  &.with-background-gradient {
    background: linear-gradient(180deg, #ddfafc 0%, rgba(245, 247, 249, 0) 77%);
  }

  &.right-content-L2Visible {
    width: ${(props) =>
      // eslint-disable-next-line no-nested-ternary
      props.isNetchexEmployeeNavbar
        ? props.showNetchexSidebar
          ? `calc(100vw - ${NETCHEX_SIDEPANEL_WIDTH})`
          : '100vw'
        : `calc(100vw - ${L1_L2_NAV_WIDTH})`};
  }
  &.right-content-L2Visible-mobile {
    width: 100vw;
  }
  transition: 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  overflow: auto;
  padding: 8px 16px;
  padding-bottom: 12px;
`;

const HorizontalCollapse = styled(Box)`
  flex-grow: 1;
  /* width: 320px; */
  overflow: visible;
  position: relative;
  left: 0px;
  visibility: visible;
`;

const App = ({ isAuthenticated }: AppProps) => {
  // @todo this seems to deprecated and of no use along with context in which this is used, and all usages after getting from context
  // useScrollToTopOnPathChange added below does the scrolling to top thing at one place.
  // to be checked and removed if not needed
  const scrollParentRef = useRef(null);
  const ref = useRef(null);
  // const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  // const userRole = getProfileRole();
  const dispatch = useDispatch();
  // const employeeObj = useSelector(selectLoggedInEmployeeData);

  useScrollToTopOnPathChange(ref);

  // line 127, 128 To be removed once the preview banner is removed
  // we can not use checkNewNavEnabled FF here because we have done some modification in FF when preview is enabled
  const enabledFeatures = selectEnabledFeatureFlags(store.getState()) ?? [];
  const checkNewNavEnabled = enabledFeatures.includes(FEATURE_FLAGS.NEW_NAV.key);

  const fetchVisierAuthToken = async () => {
    try {
      const url = deerEndpoints.analytics.authToken;
      // @ts-expect-error send request is not typed
      const res = await sendRequest({
        url,
        method: 'GET',
        credentials: 'include',
      });
      const visierAuthToken = res?.entity?.visierAccessToken;
      dispatch(setVisierAuthToken(visierAuthToken));
    } catch (error) {
      // do nothing as already handled in sendRequest for logging purpose.
      // this failing is a valid case in case the visier analytics is not enabled for the org
    }
  };

  const { l1Module } = useRouteToNavLevels();

  const isMyDataModule = l1Module === 'myData';

  const isL2Visible = useSelectIsL2Visible();
  const unsavedAlertProps = useUnsavedDataAlertProps();

  const { isExact: isHome } = useRouteMatch(staticRoutes.home);

  useOneOnOneRedirection();
  useDeerRabbitRedirection();
  const { loadingComplete } = useLoggedInUsersAccessesQuery();

  const accesses = getUserL1Access();

  const isExpandedContainerExpanded = useSelectIsExpanded();

  const isNetchexMobileSupportedPage = useIsNetchexMobileSupportedPage();

  const { isNetchexNewSideNav, isNetchexNewSidePanelCollapsed } = useIsNetchexNewNavAndAdminOrManagerPage();
  const isNetchexNewSidebarInternalPage = useIsNetchexNewSidebarInternalPage();

  const addMarginForNetchexIconSidebar = isNetchexNewSideNav && !isNetchexNewSidebarInternalPage;

  useEffect(() => {
    if (!isNetchexLogin && (checkVisierAnalyticsEnabled() || checkVisierReportsEnabled())) {
      fetchVisierAuthToken();
    }
  }, [dispatch]);

  useEffect(() => {
    if (isNetchexLogin) {
      dispatch(setL2Visible({ isL2Visible: true }));
    }
  }, [dispatch]);

  if (isAuthenticated !== null && !isAuthenticated) return <Redirect to={{ pathname: staticRoutes.login }} />;
  if (!loadingComplete) return null;

  if (isMobileView() && isNetchexLogin && !isNetchexMobileSupportedPage) {
    return (
      <>
        <NetchexPrimaryNav />
        <NoMobilePageSupportNetchexMessage />
      </>
    );
  }

  return (
    <>
      {checkNewNavPreviewFeatureEnabled() && <DeerBanner />}
      <GoalsWeightProvider>
        <ScrollContext.Provider value={scrollParentRef}>
          <Box
            pt={checkNewNavEnabled ? '0px' : '12px'}
            display="flex"
            w={
              addMarginForNetchexIconSidebar
                ? `calc( 100vw - ${isNetchexNewSidePanelCollapsed ? NETCHEX_NEW_ICON_ONLY_SIDEPANEL_WIDTH : NETCHEX_SIDEPANEL_WIDTH})`
                : '100vw'
            } // to not let the content shift when srollbar appears. REFER: https://www.reddit.com/r/webdev/comments/svfsl3/comment/hxgmhft/
            marginLeft={
              addMarginForNetchexIconSidebar
                ? isNetchexNewSidePanelCollapsed
                  ? NETCHEX_NEW_ICON_ONLY_SIDEPANEL_WIDTH
                  : NETCHEX_SIDEPANEL_WIDTH
                : '0'
            }
            justifyContent="flex-end"
            className="deer"
            flexDirection={isMobileView() ? 'column' : 'row'}
          >
            <HorizontalCollapse
              className="left-content-root"
              zIndex={isExpandedContainerExpanded ? 1 : 2}
              w={
                isNetchexLogin && isNetchexNewSidebarInternalPage && !isMobileView()
                  ? NETCHEX_SIDEPANEL_WIDTH
                  : undefined
              }
              // w={isNetchexLogin && isMyDataModule ? NETCHEX_SIDEPANEL_WIDTH : undefined}
            >
              <Box
                sx={{
                  overflowY: 'visible',
                  position: isNetchexLogin ? (isMobileView() ? 'static' : 'sticky') : 'fixed',
                  left: 0,
                  top: isNetchexLogin ? 0 : 'auto',
                  width: isMobileView() && isL2Visible ? '100%' : '',
                }}
              >
                <Box h="100%" position="relative">
                  <LeftNav />
                </Box>
              </Box>
            </HorizontalCollapse>
            <StyledRightContentBox
              ref={ref}
              className={`${
                isMobileView() ? 'right-content-root-mobile' : 'right-content-root'
              } ${!isL2Visible ? '' : isMobileView() ? 'right-content-L2visible-mobile' : 'right-content-L2Visible'} ${
                isHome ? 'with-background-gradient' : ''
              }`}
              isL2Visible={isL2Visible}
              isNetchexEmployeeNavbar={isNetchexLogin}
              showNetchexSidebar={isMyDataModule || isNetchexNewSidebarInternalPage}
            >
              <Switch>
                <PrivateRoute exact path={staticRoutes.home} component={Home} />
                <PrivateRoute exact path={staticRoutes.appHome} component={Home} />
                <PrivateRoute exact path={staticRoutes.profile} component={Profile} />
                <PrivateRoute path={L1_PATHS.myData} component={MyData} />
                <PrivateRoute path={L1_PATHS.team} component={Team} />
                {accesses.hasL1CompanyAccess ? <PrivateRoute path={L1_PATHS.company} component={Company} /> : null}

                {Object.keys(NON_DEER_ROUTES).map((key) => {
                  const { route, component: Component } = NON_DEER_ROUTES[key];
                  return <PrivateRoute key={route} exact path={route} component={Component} />;
                })}

                <PrivateRoute path={staticRoutes.visierLogin} component={VisierLogin} />

                <Route path="*" component={PageNotFound404} />
              </Switch>

              {/* date pickers */}
              <DatePickerComponentWithRedux />
              <DateTimePickerComponentWithRedux />

              {/* Views panels */}
              <ViewsWrapper />

              {/* Review panels */}
              <NominatePeersSidePanelV1 />

              {/* Engagement panel */}

              {getUserL2Access().hasEngagementAccess && <AddQuestionBottomDrawer />}

              {/* OKR PANELS */}
              {getUserL2Access().hasGoalAccess && <OKRSidePanel />}
              {getUserL2Access().hasGoalAccess && <OKRCreationPanel />}
              {getUserL2Access().hasGoalAccess && <GoalBulkUploadPanel />}
              {getUserL2Access().hasGoalAccess && <ObjectiveBulkUploadPanel />}
              <InitiativeSidePanel />
              <InitiativeCreationPanel />

              {/* OKR PANELS END */}
              {/* 1-1 PANEL */}
              {getUserL2Access().hasOneOnOneAccess && <CreateOneOnOneDrawer />}

              {/* 1-1 PANEL END */}
              {/* Competency PANELS */}
              {getUserL2Access().hasFeedbackAccess && <CompetencyDrawer />}
              {/* Competency PANELS END */}
              {getUserL2Access().hasFeedbackAccess && <ContinuousFeedbackSidebar />}

              <QuestionDetailsSidePanel />
              {getUserL2Access().hasReviewAccess && <CalibrationViewSidepanel />}
              {getUserL2Access().hasReviewAccess && <ReviewManageSidepanel />}

              {(getUserL2Access().hasReviewAccess || getUserL2Access().hasGoalAccess) && <GoalsWeightSidepanelV1 />}
              {getUserL2Access().hasDisciplinaryActionsAccess && <ViewViolationDrawer />}

              {/* Modals */}
              <AlertDialogComponent
                {...unsavedAlertProps}
                key={String(unsavedAlertProps?.isUnsavedDataAlertDialogVisible)}
                isOpen={unsavedAlertProps?.isUnsavedDataAlertDialogVisible}
                isTriggerRequired={false}
              />
              {!isMobileView() && <GlobalSearch />}
              <CreateViolationDrawer />
            </StyledRightContentBox>
          </Box>
          <GifModal />
          {/* <WindowInnerHeightLogger /> */}
        </ScrollContext.Provider>
      </GoalsWeightProvider>
    </>
  );
};

export default App;
